
import React, { useEffect, useState } from 'react';
import { isValidUrl } from '@/lib/utils';

interface ImageHandlerProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: string;
  size?: 'thumbnail' | 'medium' | 'original';
  onError?: () => void;
}

// Default fallback image in base64
const DEFAULT_FALLBACK = "data:image/png;base64,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";

/**
 * Checks if a URL is valid
 * @param url The URL to check
 * @returns True if the URL is valid
 */
const checkValidUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return isValidUrl(url);
};

/**
 * Handles image display with fallback and error handling
 * Works with both Supabase Storage URLs and regular URLs
 */
const ImageHandler: React.FC<ImageHandlerProps> = ({ 
  src, 
  alt, 
  className = '', 
  fallback = DEFAULT_FALLBACK,
  size = 'original',
  onError,
}) => {
  const [imageSrc, setImageSrc] = useState<string>(fallback);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        
        console.log('[ImageHandler] Loading image:', { src, size, alt });
        
        // If src is undefined, empty or not a valid URL, use fallback
        if (!src || !checkValidUrl(src)) {
          console.log('[ImageHandler] Invalid URL, using fallback:', { src, fallback });
          setImageSrc(fallback);
          setError(true);
          setLoading(false);
          return;
        }

        // Check if it's a Supabase URL and apply size modifications if needed
        if (src.includes('storage.googleapis.com') || src.includes('supabase.co/storage/v1/object/public')) {
          console.log('[ImageHandler] Supabase URL detected');
          // For Supabase URLs, use the original image
          // Size modifications are disabled as resized versions may not exist
          console.log('[ImageHandler] Using original Supabase URL (size modifications disabled)');
          setImageSrc(src);
        } else {
          // For other URLs, use as is
          console.log('[ImageHandler] Regular URL:', src);
          setImageSrc(src);
        }
      } catch (error) {
        console.error('[ImageHandler] Error loading image:', error);
        setImageSrc(fallback);
        setError(true);
        if (onError) onError();
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [src, fallback, size, onError]);

  const handleError = () => {
    console.log('[ImageHandler] Image failed to load, using fallback');
    setImageSrc(fallback);
    setError(true);
    if (onError) onError();
  };

  if (loading) {
    console.log('[ImageHandler] Rendering loading state');
    return (
      <div className={`bg-gray-200 animate-pulse ${className}`}>
        <span className="sr-only">Carregando...</span>
      </div>
    );
  }

  console.log('[ImageHandler] Rendering image:', { imageSrc, error, loading });
  return (
    <img 
      src={imageSrc} 
      alt={alt} 
      className={className} 
      onError={handleError}
    />
  );
};

export default ImageHandler;
